/* eslint-disable react/display-name */
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { DocumentTabs, ISourceDocument, MappingSessionRecord } from '../../../../../types';
import { getDate } from '../../../../../helpers/catalogHelpers';
import { TableHeader } from '@GxT-Team/gxt-components';
import { CatalogSortParams, MappingType, SortOrderType } from '../../../../../services/api/types';
import { getUserFullName } from '../../../../../helpers/utils';
import { ItemType } from '../../../../../services/api/types';
import { formatCellData } from './helper';
import { CatalogItemsHash } from '../../../../upload';

export const generateColumnsByTab = (
  activeTab: DocumentTabs,
  handleColumnSort: (sort: SortOrderType, column: keyof CatalogSortParams) => void,
  sortedColumn: { column: string; order: SortOrderType },
) => {
  const catalogColumnHelper = createColumnHelper<ISourceDocument>();
  const mappingSessionColumnHelper = createColumnHelper<MappingSessionRecord>();

  const createTableHeader = (
    title: string,
    accessor = '',
    activeColumn = { column: '', order: 'ASC' as SortOrderType },
  ) => {
    return () => (
      <TableHeader
        ColumnName={accessor}
        Title={title}
        handleColumnSort={handleColumnSort as (sort: SortOrderType, column: string) => void}
        activeColumn={activeColumn}
      />
    );
  };

  const valueFormatter = (value: string) => {
    switch (value) {
      case MappingType.AUTHORING:
        return 'Manual';
      case MappingType.AI_AUTHORING:
        return 'Automated';
      default:
        return value;
    }
  };

  const CatalogItemTableCell = ({
    data,
    itemType,
  }: {
    data: CatalogItemsHash;
    itemType: ItemType;
  }) => {
    const values = formatCellData(data, itemType);
    return <div className="text-normal text-neutral-400">{values}</div>;
  };

  type CatalogTableRow = ISourceDocument | MappingSessionRecord;

  const columnsByTab = {
    [DocumentTabs.TARGET]: [
      catalogColumnHelper.accessor('id', {
        header: createTableHeader('ID', 'id', sortedColumn),
        cell: (info) => info.getValue(),
        size: 46,
      }),
      catalogColumnHelper.accessor('documentName', {
        header: createTableHeader('Document Name', 'documentName', sortedColumn),
        cell: (info) => info.getValue(),
        size: 450,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('CTD Section', 'ctdSection', sortedColumn),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.CTDSection} />
        ),
        size: 77,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('Region', 'region', sortedColumn),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.Region} />
        ),
        size: 77,
      }),
      catalogColumnHelper.accessor('version', {
        header: createTableHeader('Version', 'version', sortedColumn),
        cell: (info) => info.getValue(),
        size: 89,
      }),
      catalogColumnHelper.accessor('uploadedBy', {
        header: createTableHeader('Uploaded By', 'uploadedBy', sortedColumn),
        cell: (info) => getUserFullName(info.row.original.createdBy),
        size: 180,
      }),
    ],
    [DocumentTabs.EXTERNAL]: [
      catalogColumnHelper.accessor('id', {
        header: createTableHeader('ID', 'id', sortedColumn),
        cell: (info) => info.getValue(),
        size: 46,
      }),
      catalogColumnHelper.accessor('documentName', {
        header: createTableHeader('Document Name', 'documentName', sortedColumn),
        cell: (info) => info.getValue(),
        size: 450,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('CTD Section', 'ctdSection', sortedColumn),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.CTDSection} />
        ),
        size: 77,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('Region', 'region', sortedColumn),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.Region} />
        ),
        size: 77,
      }),
      catalogColumnHelper.accessor('version', {
        header: createTableHeader('Version', 'version', sortedColumn),
        cell: (info) => info.getValue(),
        size: 89,
      }),
      catalogColumnHelper.accessor('uploadedBy', {
        header: createTableHeader('Uploaded By', 'uploadedBy', sortedColumn),
        cell: (info) => getUserFullName(info.row.original.createdBy),
        size: 180,
      }),
    ],
    [DocumentTabs.SOURCE]: [
      catalogColumnHelper.accessor('id', {
        header: createTableHeader('ID', 'id', sortedColumn),
        cell: (info) => info.getValue(),
        size: 46,
      }),
      catalogColumnHelper.accessor('documentName', {
        header: createTableHeader('Document Name', 'documentName', sortedColumn),
        cell: (info) => info.getValue(),
        size: 420,
      }),
      catalogColumnHelper.accessor('processingStatus', {
        header: createTableHeader('Status'),
        cell: (info) => {
          const status = info.getValue();
          if (!status || status === 'active') return null;
          if (status === 'processing') {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24" fill="none">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                Processing
              </span>
            );
          }
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              Failed
            </span>
          );
        },
        size: 110,
      }),
      catalogColumnHelper.accessor('version', {
        header: createTableHeader('Version', 'version', sortedColumn),
        cell: (info) => info.getValue(),
        size: 77,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('Material'),
        cell: (info) => {
          return <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.Material} />;
        },
        size: 139,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('Process Id'),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.ProcessID} />
        ),
        size: 139,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('DP Image'),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.DPImage} />
        ),
        size: 139,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('Site'),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.Site} />
        ),
        size: 180,
      }),
      catalogColumnHelper.accessor('documentNum', {
        header: createTableHeader('Doc No', 'documentNum', sortedColumn),
        cell: (info) => info.getValue(),
        size: 120,
      }),
      catalogColumnHelper.accessor('uploadedBy', {
        header: createTableHeader('Uploaded By', 'uploadedBy', sortedColumn),
        cell: (info) => getUserFullName(info.row.original.createdBy),
        size: 139,
      }),
    ],
    [DocumentTabs.COMPLETED]: [
      mappingSessionColumnHelper.accessor('id', {
        header: createTableHeader('ID', 'id', sortedColumn),
        cell: (info) => info.getValue(),
        size: 46,
      }),
      mappingSessionColumnHelper.accessor('title', {
        header: createTableHeader('Document Name', 'title', sortedColumn),
        cell: (info) => info.getValue(),
        size: 450,
      }),
      mappingSessionColumnHelper.accessor('project', {
        header: createTableHeader('Project'),
        cell: (info) => info.getValue()?.name,
        size: 209,
      }),
      mappingSessionColumnHelper.accessor('mappingType', {
        header: createTableHeader('Type', 'mappingType', sortedColumn),
        cell: (info) => valueFormatter(info.getValue()),
        size: 209,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('Section', 'ctdSection', sortedColumn),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.CTDSection} />
        ),
        size: 77,
      }),
      mappingSessionColumnHelper.accessor('createdAt', {
        header: createTableHeader('Created On'),
        cell: (info) => {
          const createdAt = info.row.original.createdAt;
          return createdAt && getDate(createdAt);
        },
      }),
      mappingSessionColumnHelper.accessor('createdBy', {
        header: createTableHeader('Owner', 'createdBy', sortedColumn),
        cell: (info) => getUserFullName(info.row.original.createdBy),
        size: 209,
      }),
    ],
    [DocumentTabs.IN_PROGRESS]: [
      mappingSessionColumnHelper.accessor('id', {
        header: createTableHeader('ID', 'id', sortedColumn),
        cell: (info) => info.getValue(),
        size: 46,
      }),
      mappingSessionColumnHelper.accessor('title', {
        header: createTableHeader('Document Name', 'title', sortedColumn),
        cell: (info) => info.getValue(),
        size: 450,
      }),
      mappingSessionColumnHelper.accessor('project', {
        header: createTableHeader('Project'),
        cell: (info) => info.getValue()?.name,
        size: 210,
      }),
      mappingSessionColumnHelper.accessor('mappingType', {
        header: createTableHeader('Type', 'mappingType', sortedColumn),
        cell: (info) => valueFormatter(info.getValue()),
        size: 120,
      }),
      mappingSessionColumnHelper.accessor('catalogItems', {
        header: createTableHeader('CTD Section', 'ctdSections', sortedColumn),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.CTDSection} />
        ),
        size: 79,
      }),
      mappingSessionColumnHelper.accessor('status', {
        header: createTableHeader('Status', 'status', sortedColumn),
        cell: (info) => {
          const status = info.getValue();
          if (!status) return null;

          const statusLabels: Record<string, string> = {
            PENDING: 'Pending',
            GENERATING_AI_SUGGESTIONS: 'Generating Suggestions',
            READY_TO_AUTHOR: 'Ready to Author',
            READY_TO_REVIEW: 'Ready to Review',
            SUGGESTIONS_FAILED: 'Suggestions Failed',
          };

          return <span>{statusLabels[status] ?? status}</span>;
        },
        size: 235,
      }),
      mappingSessionColumnHelper.accessor('updatedAt', {
        header: createTableHeader('Last Modified', 'updatedAt', sortedColumn),
        cell: (info) => {
          const updatedAt = info.row.original.updatedAt;
          return updatedAt && getDate(updatedAt);
        },
        size: 130,
      }),
      mappingSessionColumnHelper.accessor('createdBy', {
        header: createTableHeader('Owner', 'ownedBy', sortedColumn),
        cell: (info) => getUserFullName(info.row.original.createdBy),
        size: 116,
      }),
    ],
    [DocumentTabs.AUTOMATED]: [
      catalogColumnHelper.accessor('id', {
        header: createTableHeader('ID', 'id', sortedColumn),
        cell: (info) => info.getValue(),
        size: 46,
      }),
      catalogColumnHelper.accessor('title', {
        header: createTableHeader('Document Name', 'title', sortedColumn),
        cell: (info) => info.getValue(),
        size: 450,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('CTD Section', 'catalogItems'),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.CTDSection} />
        ),
        size: 77,
      }),
      catalogColumnHelper.accessor('catalogItems', {
        header: createTableHeader('Region', 'region', sortedColumn),
        cell: (info) => (
          <CatalogItemTableCell data={info.getValue() || {}} itemType={ItemType.Region} />
        ),
        size: 77,
      }),
      catalogColumnHelper.accessor('version', {
        header: createTableHeader('Version', 'version', sortedColumn),
        cell: (info) => info.getValue(),
        size: 89,
      }),
      catalogColumnHelper.accessor('uploadedBy', {
        header: createTableHeader('Uploaded By', 'uploadedBy', sortedColumn),
        cell: (info) => getUserFullName(info.row.original.createdBy),
        size: 180,
      }),
    ],
    [DocumentTabs.PRE_AUTHORED]: [],
  } as Record<DocumentTabs, ColumnDef<CatalogTableRow, unknown>[]>;

  return columnsByTab[activeTab];
};
